import { PERMISSIONS } from "@nimbus-erp/shared";
import { Suspense, type ReactNode } from "react";
import { createBrowserRouter } from "react-router-dom";
import { ErpShell } from "./components/ErpShell";
import { UnsavedDocumentsProvider } from "./components/useUnsavedDocument";
import { DefaultBusinessRoute } from "./components/DefaultBusinessRoute";
import { AuthBootstrap } from "./components/AuthBootstrap";
import { ErpLoading } from "./components/ErpLoading";
import { PermissionGate } from "./components/PermissionGate";
import { ForbiddenPage } from "./pages/ForbiddenPage";
import { PlatformAccessPage } from "./pages/PlatformAccessPage";
import { ErpLoginPage } from "./pages/ErpLoginPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { RouteErrorPage } from "./pages/RouteErrorPage";
import { lazyWithRetry } from "./utils/lazyWithRetry";

const AnalyticsPage = lazyWithRetry(() =>
  import("./pages/AnalyticsPage").then((m) => ({ default: m.AnalyticsPage })),
);
const CustomerDataReviewPage = lazyWithRetry(() => import("./pages/CustomerDataReviewPage").then((m) => ({ default: m.CustomerDataReviewPage })));
const TaskCenterPage = lazyWithRetry(() =>
  import("./pages/TaskCenterPage").then((module) => ({
    default: module.TaskCenterPage,
  })),
);
const WorkbenchPage = lazyWithRetry(() =>
  import("./pages/WorkbenchPage").then((module) => ({
    default: module.WorkbenchPage,
  })),
);
const ProductionPage = lazyWithRetry(() =>
  import("./pages/ProductionPage").then((module) => ({
    default: module.ProductionPage,
  })),
);
const WorkflowPage = lazyWithRetry(() =>
  import("./pages/WorkflowPage").then((module) => ({
    default: module.WorkflowPage,
  })),
);
const EquipmentPage = lazyWithRetry(() =>
  import("./pages/EquipmentPage").then((module) => ({
    default: module.EquipmentPage,
  })),
);
const QualityPage = lazyWithRetry(() =>
  import("./pages/QualityPage").then((module) => ({
    default: module.QualityPage,
  })),
);
const PlanningPage = lazyWithRetry(() =>
  import("./pages/PlanningPage").then((module) => ({
    default: module.PlanningPage,
  })),
);
const ManufacturingPage = lazyWithRetry(() =>
  import("./pages/ManufacturingPage").then((module) => ({
    default: module.ManufacturingPage,
  })),
);
const OrganizationPage = lazyWithRetry(() =>
  import("./pages/OrganizationPage").then((module) => ({
    default: module.OrganizationPage,
  })),
);
const SubscriptionPage = lazyWithRetry(() =>
  import("./pages/SubscriptionPage").then((module) => ({
    default: module.SubscriptionPage,
  })),
);
const AccessPage = lazyWithRetry(() =>
  import("./pages/AccessPage").then((module) => ({
    default: module.AccessPage,
  })),
);
const MasterDataPage = lazyWithRetry(() =>
  import("./pages/MasterDataPage").then((module) => ({
    default: module.MasterDataPage,
  })),
);
const EnterpriseReferencesPage = lazyWithRetry(() =>
  import("./pages/EnterpriseReferencesPage").then((module) => ({
    default: module.EnterpriseReferencesPage,
  })),
);
const SalesPage = lazyWithRetry(() =>
  import("./pages/SalesPage").then((module) => ({
    default: module.SalesPage,
  })),
);
const PurchasePage = lazyWithRetry(() =>
  import("./pages/PurchasePage").then((module) => ({
    default: module.PurchasePage,
  })),
);
const InventoryPage = lazyWithRetry(() =>
  import("./pages/InventoryPage").then((module) => ({
    default: module.InventoryPage,
  })),
);
const SettlementPage = lazyWithRetry(() =>
  import("./pages/SettlementPage").then((module) => ({
    default: module.SettlementPage,
  })),
);
const FinancePage = lazyWithRetry(() =>
  import("./pages/FinancePage").then((module) => ({
    default: module.FinancePage,
  })),
);

const secured = (element: ReactNode) => (
  <AuthBootstrap>
    <UnsavedDocumentsProvider>{element}</UnsavedDocumentsProvider>
  </AuthBootstrap>
);
const deferred = (element: ReactNode) => (
  <Suspense
    fallback={<ErpLoading mode="route" label="页面加载中" />}
  >
    {element}
  </Suspense>
);

export const router = createBrowserRouter([
  {path:"/platform-access",element:<PlatformAccessPage />,errorElement:<RouteErrorPage />},
  {
    path: "/login",
    element: <ErpLoginPage />,
    errorElement: <RouteErrorPage />,
  },
  {
    path: "/",
    element: secured(<ErpShell />),
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <DefaultBusinessRoute /> },
      { path: "customer-data-review", element: deferred(<PermissionGate permission={PERMISSIONS.organizationManage}><PermissionGate permission={PERMISSIONS.costPriceRead}><CustomerDataReviewPage /></PermissionGate></PermissionGate>) },
      {
        path: "analytics",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.analyticsRead}>
            <AnalyticsPage />
          </PermissionGate>,
        ),
      },
      {
        path: "tasks",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.taskCenterRead}>
            <TaskCenterPage />
          </PermissionGate>,
        ),
      },
      {
        path: "workflow",
        element: (
          <PermissionGate permission={PERMISSIONS.workflowRead}>
            {deferred(<WorkflowPage />)}
          </PermissionGate>
        ),
      },
      {
        path: "equipment",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.equipmentRead}>
            <EquipmentPage />
          </PermissionGate>,
        ),
      },
      {
        path: "quality",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.qualityRead}>
            <QualityPage />
          </PermissionGate>,
        ),
      },
      {
        path: "planning",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.planningRead}>
            <PlanningPage />
          </PermissionGate>,
        ),
      },
      {
        path: "production",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.manufacturingRead}>
            <ProductionPage />
          </PermissionGate>,
        ),
      },
      {
        path: "manufacturing",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.manufacturingRead}>
            <ManufacturingPage />
          </PermissionGate>,
        ),
      },
      {
        path: "workspace",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.dashboardView}>
            <WorkbenchPage />
          </PermissionGate>,
        ),
      },
      {
        path: "sales",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.salesRead}>
            <SalesPage />
          </PermissionGate>,
        ),
      },
      {
        path: "purchase",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.purchaseRead}>
            <PurchasePage />
          </PermissionGate>,
        ),
      },
      {
        path: "inventory",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.inventoryRead}>
            <InventoryPage />
          </PermissionGate>,
        ),
      },
      {
        path: "settlement",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.settlementRead}>
            <SettlementPage />
          </PermissionGate>,
        ),
      },
      {
        path: "finance",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.financeRead}>
            <FinancePage />
          </PermissionGate>,
        ),
      },
      {
        path: "master-data",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.masterDataRead}>
            <MasterDataPage />
          </PermissionGate>,
        ),
      },
      {
        path: "master-data/references",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.masterDataRead}>
            <EnterpriseReferencesPage />
          </PermissionGate>,
        ),
      },
      {
        path: "organization/positions",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.organizationRead}>
            <EnterpriseReferencesPage positions />
          </PermissionGate>,
        ),
      },
      {
        path: "organization",
        element: deferred(
          <PermissionGate permission={PERMISSIONS.organizationRead}>
            <OrganizationPage />
          </PermissionGate>,
        ),
      },
      { path: "subscription", element: deferred(<SubscriptionPage />) },
      { path: "access", element: deferred(<AccessPage />) },
      { path: "403", element: <ForbiddenPage /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
